import React from 'react';
import { Bell, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePendingJobs } from '@/hooks/usePendingJobs';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

export function NotificationBell() {
  const { dlqJobs = [], recentlyCompleted = [], totalPending = 0, isLoading } = usePendingJobs();
  const navigate = useNavigate();

  // Ensure arrays
  const dlqJobsArray = Array.isArray(dlqJobs) ? dlqJobs : [];
  const recentlyCompletedArray = Array.isArray(recentlyCompleted) ? recentlyCompleted : [];

  const totalNotifications = totalPending + recentlyCompletedArray.length;

  const handleJobClick = (jobId: string) => {
    // Navigate to session history with job filter
    navigate(`/sessions/history?job=${jobId}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {totalNotifications > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {totalNotifications > 9 ? '9+' : totalNotifications}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {isLoading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading notifications...
          </div>
        ) : totalNotifications === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No new notifications
          </div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto">
            {/* DLQ Jobs - Pending retry */}
            {dlqJobsArray.length > 0 && (
              <>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  Pending Retry
                </div>
                {dlqJobsArray.map((job) => (
                  <DropdownMenuItem
                    key={job.id}
                    onClick={() => handleJobClick(job.id)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-start gap-3 w-full">
                      <Clock className="h-4 w-4 mt-1 text-orange-500" />
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">
                          Audio service temporarily unavailable
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Will retry automatically •{' '}
                          {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))}
                {recentlyCompletedArray.length > 0 && <DropdownMenuSeparator />}
              </>
            )}

            {/* Recently Completed Jobs */}
            {recentlyCompletedArray.length > 0 && (
              <>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  Recently Completed
                </div>
                {recentlyCompletedArray.map((job) => (
                  <DropdownMenuItem
                    key={job.id}
                    onClick={() => handleJobClick(job.id)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-start gap-3 w-full">
                      <CheckCircle className="h-4 w-4 mt-1 text-green-500" />
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">Meditation ready!</p>
                        <p className="text-xs text-muted-foreground">
                          {job.result_data?.retried_from_dlq && 'Completed from retry • '}
                          {formatDistanceToNow(new Date(job.completed_at!), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </>
            )}
          </div>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/sessions/history')} className="cursor-pointer">
          <span className="text-sm font-medium text-primary">View all sessions</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
